import * as z from "@/lib/zod";

export const PLAN_VERSION = "betting_plan_v1";
export const PLAN_STORAGE_KEY = "upsider_betting_plan_v1";
export const PLAN_APPS = ["prizepicks", "underdog", "dabble", "dkpick6", "betr", "draftkings", "fanduel", "novig", "prophetx", "kalshi", "fliff", "none"] as const;
export const BANKROLL_OPTIONS = [
  { value: "undecided", label: "Still deciding" },
  { value: "under_250", label: "Under $250" },
  { value: "250_999", label: "$250–$999" },
  { value: "1000_4999", label: "$1,000–$4,999" },
  { value: "5000_plus", label: "$5,000+" },
] as const;
export const TIME_OPTIONS = [
  { value: "15", label: "15 min" },
  { value: "30", label: "30 min" },
  { value: "60", label: "1 hour" },
  { value: "120", label: "2+ hours" },
] as const;
export const PlanAnswersSchema = z.object({
  apps: z.array(z.enum(PLAN_APPS)).min(1).max(PLAN_APPS.length).refine((apps) => new Set(apps).size === apps.length && (!apps.includes("none") || apps.length === 1)),
  bankroll: z.enum(["undecided", "under_250", "250_999", "1000_4999", "5000_plus"]),
  time: z.enum(["15", "30", "60", "120"]),
});
export type PlanAnswers = z.infer<typeof PlanAnswersSchema>;
export const PlanDraftSchema = z.object({
  apps: z.array(z.enum(PLAN_APPS)).max(PLAN_APPS.length),
  bankroll: z.union([PlanAnswersSchema.shape.bankroll, z.literal("")]),
  time: z.union([PlanAnswersSchema.shape.time, z.literal("")]),
  step: z.number().int().min(0).max(3),
  captured: z.boolean().optional(),
  complete: z.boolean().optional(),
});

export function getBettingPlan(answers: PlanAnswers) {
  const fantasyApp = ["prizepicks", "underdog", "dabble", "dkpick6", "betr"].find((app) => answers.apps.some((selected) => selected === app));
  const learning = answers.apps.includes("none") || answers.bankroll === "undecided";
  const tool = learning ? "Free learning center" : fantasyApp ? "Fantasy Props Optimizer" : "+EV Sniper";
  const destination = learning ? "/learning-center" : fantasyApp ? `/optimizer/props?sportsbook=${fantasyApp}` : "/ev-sniper";
  const reason = learning
    ? "Start with how prices and payouts work before choosing a tool or putting money at risk."
    : fantasyApp
      ? "You use a pick’em app. Start by comparing its offers with the wider market, rather than relying on recent hit streaks."
      : "You use sportsbooks or exchanges. Start with a view that compares the offered price with a market estimate.";
  const routine = answers.time === "15" || answers.time === "30"
    ? "Keep it focused: choose one app and one sport, then compare a small shortlist. You can stop without placing a bet."
    : "Use your extra research time to compare prices across your selected apps, then review your tracking log. More time does not require more bets.";
  const bankroll = answers.bankroll === "undecided"
    ? "Decide whether betting fits your budget first. Explore examples without depositing or wagering."
    : answers.bankroll === "under_250"
      ? "With a smaller bankroll, check whether tool costs and app minimums fit your budget. Start with free lessons if they do not."
      : "Set a fixed loss limit within your chosen budget. Keep living expenses separate and never increase the budget to chase a loss.";
  return { tool, destination, reason, routine, bankroll, learning };
}
